import React from 'react';
import {
    Target,
    Users,
    Globe,
    Award,
    BookOpen,
    TrendingUp,
    CheckCircle,
    Star,
    GraduationCap,
    Building,
    MapPin,
    Phone,
    Mail,
    Calendar,
    Briefcase,
    Network
} from 'lucide-react';
import ResponsiveNavbar from './ResponsiveNavbar';

const AboutPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Navigation */}
            <ResponsiveNavbar />

            {/* Hero Section */}
            <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                        🏢 About SmeHub Consulting
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Empowering Success Through
                        <span className="bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent"> Expert Guidance</span>
                    </h1>
                    <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                        At SmeHub Consulting Limited, we are dedicated to providing exceptional corporate and educational
                        consulting services that enable our clients to succeed in both complex and non-complex environments.
                    </p>
                </div>
            </section>

            {/* Mission Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Mission</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Providing high-quality, actionable insights and strategies that drive tangible results
                        </p>
                    </div>

                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 sm:p-12 text-white mb-16">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-8">
                                <Target className="w-10 h-10" />
                            </div>
                            <p className="text-xl sm:text-2xl leading-relaxed text-white">
                                Our mission is to provide exceptional corporate and educational consulting services that enable our clients
                                to succeed in both complex and non-complex environments through the development of high-quality, actionable
                                and proven insights and strategies that drive tangible results and create lasting impact.
                            </p>
                        </div>
                    </div>

                    {/* Core Values */}
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Target className="w-8 h-8" />,
                                title: 'Innovation',
                                description: 'In a rapidly changing world, innovation is key to staying ahead of the curve. We embrace the latest technologies, methodologies, and best practices to deliver cutting-edge solutions that drive innovation and foster growth.',
                                color: 'from-blue-500 to-blue-600'
                            },
                            {
                                icon: <Users className="w-8 h-8" />,
                                title: 'Collaboration',
                                description: 'We believe in the power of collaboration and partnership. By working closely with our clients, we foster open communication, transparency, and mutual trust, ensuring that we fully understand their needs and objectives.',
                                color: 'from-indigo-500 to-indigo-600'
                            },
                            {
                                icon: <Globe className="w-8 h-8" />,
                                title: 'Customized Solutions',
                                description: 'We recognize that every client is unique, with distinct objectives and challenges. That\'s why we take a personalized approach to our services, tailoring our solutions to address specific needs and goals.',
                                color: 'from-purple-500 to-purple-600'
                            }
                        ].map((value, index) => (
                            <div key={index} className="group p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-blue-50 hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 hover:shadow-lg">
                                <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {value.icon}
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Expertise Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Expertise</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            A team of highly qualified professionals with extensive experience across multiple disciplines
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <GraduationCap className="w-8 h-8" />,
                                title: 'Career Development & Management',
                                description: 'Expert guidance in career planning, development strategies, and management consulting.'
                            },
                            {
                                icon: <Building className="w-8 h-8" />,
                                title: 'Business Engineering',
                                description: 'Strategic business planning, process optimization, and organizational engineering.'
                            },
                            {
                                icon: <TrendingUp className="w-8 h-8" />,
                                title: 'Growth Strategies',
                                description: 'Development of sustainable growth strategies and market expansion plans.'
                            },
                            {
                                icon: <BookOpen className="w-8 h-8" />,
                                title: 'Research Methodology',
                                description: 'Advanced research methodologies and analytical frameworks for informed decision-making.'
                            },
                            {
                                icon: <Target className="w-8 h-8" />,
                                title: 'Data Analysis',
                                description: 'Comprehensive data analysis and insights generation for strategic planning.'
                            },
                            {
                                icon: <Users className="w-8 h-8" />,
                                title: 'Educational Consulting',
                                description: 'Specialized educational consulting services for institutions and individuals.'
                            }
                        ].map((expertise, index) => (
                            <div key={index} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 group">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
                                    {expertise.icon}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                                    {expertise.title}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">{expertise.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Director Profile Section */}
          

            {/* Company Stats */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-700">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Impact</h2>
                        <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                            Delivering measurable results and creating lasting impact for our clients
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { number: '20+', label: 'Years Experience', icon: <Calendar className="w-8 h-8" /> },
                            { number: '500+', label: 'Students Helped', icon: <GraduationCap className="w-8 h-8" /> },
                            { number: '50+', label: 'Partner Institutions', icon: <Building className="w-8 h-8" /> },
                            { number: '98%', label: 'Success Rate', icon: <TrendingUp className="w-8 h-8" /> }
                        ].map((stat, index) => (
                            <div key={index} className="text-center group">
                                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                    {stat.icon}
                                </div>
                                <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</div>
                                <div className="text-blue-100">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Choose SmeHub?</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            We are adequately equipped with a wealth of knowledge and expertise to undertake any project
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Unparalleled Quality & Accuracy</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Our team comprises highly qualified professionals with extensive experience across multiple disciplines.
                                We are adequately equipped with a wealth of knowledge and expertise to undertake any and every project
                                regardless of the magnitude, and ensure unparalleled quality and accuracy.
                            </p>
                            <ul className="space-y-3">
                                {[
                                    'Proven track record of success',
                                    'Comprehensive industry expertise',
                                    'Personalized approach to every client',
                                    'Latest technologies and methodologies',
                                    'Commitment to excellence and innovation'
                                ].map((point, index) => (
                                    <li key={index} className="flex items-center">
                                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                        <span className="text-gray-700">{point}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Global Reach, Local Expertise</h3>
                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white mr-4 flex-shrink-0">
                                        <MapPin className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Nigeria Office</h4>
                                        <p className="text-gray-600 text-sm">Strategic location in Lagos with deep understanding of local market dynamics</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white mr-4 flex-shrink-0">
                                        <Globe className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Canada Presence</h4>
                                        <p className="text-gray-600 text-sm">Direct access to Canadian institutions and immigration expertise</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white mr-4 flex-shrink-0">
                                        <Network className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Partner Network</h4>
                                        <p className="text-gray-600 text-sm">Extensive network of partner institutions and service providers</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-100">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                        Ready to Transform Your Future?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Join hundreds of successful students and businesses who have achieved their goals with our expert guidance and support.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href='/consultations' className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all duration-300 shadow-lg">
                            Schedule Consultation
                        </a>
                        <a href='/service' className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-blue-500 hover:text-blue-600 transition-all duration-300">
                            Learn More About Our Services
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-12">
                        <div>
                            <div className="flex items-center space-x-2 mb-6">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">S</span>
                                </div>
                                <span className="text-xl font-bold">SmeHub</span>
                            </div>
                            <p className="text-gray-400 leading-relaxed">
                                Your trusted partner in educational consulting, immigration services, and comprehensive business solutions.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Services</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li>Student Enrollment</li>
                                <li>Immigration Services</li>
                                <li>Educational Consulting</li>
                                <li>Business Planning</li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Contact Nigeria</h4>
                            <div className="space-y-3 text-gray-400">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">2nd floor, Oludaisi Ogundare House, 19/21 Balogun street, Anifowose, Ikeja, Lagos</span>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 mr-2" />
                                    <span>+234 812 2654683</span>
                                </div>
                                <div className="flex items-center">
                                    <Mail className="w-5 h-5 mr-2" />
                                    <span>enquiry@smehub.ng</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4">Contact Canada</h4>
                            <div className="space-y-3 text-gray-400">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">4516 Valiant Drive, NW, Calgary, T3A 0Y1, Alberta</span>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 mr-2" />
                                    <span>+1 825 935 7540</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
                        <p>&copy; 2025 SmeHub Consulting Limited. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;